import React, { useEffect, useState } from 'react';
import CustomerVerificationForm from './CustomerVerificationForm';

/**
 * AccountDetailsModal
 * 
 * This component displays detailed information about an account
 * in a modal dialog.
 * 
 * Props:
 * - showModal: boolean to control visibility
 * - onClose: function to call when modal is closed
 * - selectedAccount: the account object with basic information
 * - accountDetails: detailed account information fetched from API
 * - errorMessage: error message if any occurred during fetch
 * - onVerificationSuccess: callback function to refresh data after verification
 */
const AccountDetailsModal = ({ 
    showModal, 
    onClose, 
    selectedAccount, 
    accountDetails, 
    errorMessage,
    onVerificationSuccess
}) => {
    const [showVerificationForm, setShowVerificationForm] = useState(false);

    // When modal opens/closes, control body overflow to prevent content from showing
    useEffect(() => {
        if (showModal) {
            // Prevent scrolling when modal is open
            document.body.style.overflow = 'hidden';
            
            // Add event listener for Escape key
            const handleEscape = (event) => {
                if (event.key === 'Escape') {
                    onClose();
                }
            };
            
            document.addEventListener('keydown', handleEscape);
            
            // Clean up event listener when modal closes or component unmounts
            return () => {
                document.removeEventListener('keydown', handleEscape);
                document.body.style.overflow = 'auto';
            };
        } else {
            // Restore scrolling when modal is closed
            document.body.style.overflow = 'auto';
        }
    }, [showModal, onClose]);

    if (!showModal) return null;

    // Handler for verification form submission success
    const handleVerificationSubmitSuccess = (result) => {
        // Optionally, handle result if needed
        if (onVerificationSuccess && typeof onVerificationSuccess === 'function') {
            onVerificationSuccess();
        }
        // Close the verification form modal
        setShowVerificationForm(false);
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.9)', // Made darker to better hide content behind
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999 // Increased z-index to ensure it's above everything
        }}
        onClick={onClose}
        >
            <div style={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                padding: '25px',
                borderRadius: '8px',
                minWidth: '80%',
                maxWidth: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                color: '#e0e0e0',
                boxShadow: '0 5px 15px rgba(0,0,0,0.5)',
                border: '1px solid #333',
                position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
            >
                <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid #444',
                    paddingBottom: '10px'
                }}>
                    <h2 style={{ color: '#fff', margin: 0, fontWeight: '500' }}>
                        Account Details: {selectedAccount?.firstName} {selectedAccount?.lastName}
                    </h2>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <button
                            onClick={() => setShowVerificationForm(true)}
                            style={{
                                fontSize: '16px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                cursor: 'pointer',
                                padding: '0.5rem 1rem',
                                border: 'none',
                                borderRadius: '4px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#0069d9'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                        >
                            Verification
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                fontSize: '20px',
                                backgroundColor: '#333', 
                                color: '#fff',
                                cursor: 'pointer',
                                width: '36px',
                                height: '36px',
                                border: 'none',
                                borderRadius: '50%',
                                transition: 'background-color 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#555'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#333'}
                        >
                            ✕
                        </button>
                    </div>
                </div>
                
                {!accountDetails && errorMessage ? (
                    <div style={{ color: '#ff6b6b', padding: '20px', textAlign: 'center' }}>
                        {errorMessage}
                    </div>
                ) : !accountDetails ? (
                    <div style={{ textAlign: 'center', padding: '30px' }}>
                        <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading account details...</div>
                        <div style={{ width: '40px', height: '40px', border: '3px solid #444', borderTopColor: '#0099ff', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
                        <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: '25px' }}>
                            <h3 style={{ borderBottom: '1px solid #444', paddingBottom: '10px', color: '#0099ff' }}>
                                Basic Information
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', padding: '10px' }}>
                                <div><strong style={{ color: '#9e9e9e' }}>Account #:</strong> {accountDetails.account_number || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>First Name:</strong> {accountDetails.first_name || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Last Name:</strong> {accountDetails.last_name || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Cell #:</strong> {accountDetails.cell_no || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Home #:</strong> {accountDetails.home_no || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Work #:</strong> {accountDetails.work_no || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Email:</strong> {accountDetails.email_address || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Date Completed:</strong> {accountDetails.date_completed || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Comments:</strong> {accountDetails.comments || 'N/A'}</div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '25px' }}>
                            <h3 style={{ borderBottom: '1px solid #444', paddingBottom: '10px', color: '#0099ff' }}>
                                Vehicle Information
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', padding: '10px' }}>
                                <div><strong style={{ color: '#9e9e9e' }}>Year:</strong> {accountDetails.year || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Make:</strong> {accountDetails.make || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Model:</strong> {accountDetails.model || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Color:</strong> {accountDetails.color || 'N/A'}</div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '25px' }}>
                            <h3 style={{ borderBottom: '1px solid #444', paddingBottom: '10px', color: '#0099ff' }}>
                                Financial Information
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', padding: '10px' }}>
                                <div><strong style={{ color: '#9e9e9e' }}>Payment Amount:</strong> ${accountDetails.pmt_amt || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Principal:</strong> ${accountDetails.principal || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Frequency:</strong> {accountDetails.frequency || 'N/A'}</div>
                                <div><strong style={{ color: '#9e9e9e' }}>Buy:</strong> {accountDetails.buy || 'N/A'}</div>
                            </div>
                        </div>

                        {accountDetails.diagnosis && (
                            <div style={{ marginBottom: '25px' }}>
                                <h3 style={{ borderBottom: '1px solid #444', paddingBottom: '10px', color: '#0099ff' }}>
                                    Diagnosis
                                </h3>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(2, 1fr)', 
                                    gap: '15px',
                                    padding: '15px',
                                    backgroundColor: accountDetails.diagnosis.classification === 'green' ? 'rgba(46, 125, 50, 0.2)' :
                                                    accountDetails.diagnosis.classification === 'yellow' ? 'rgba(251, 192, 45, 0.2)' :
                                                    accountDetails.diagnosis.classification === 'red' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(66, 66, 66, 0.3)',
                                    borderRadius: '6px',
                                    border: '1px solid ' + (
                                        accountDetails.diagnosis.classification === 'green' ? '#388e3c' :
                                        accountDetails.diagnosis.classification === 'yellow' ? '#fbc02d' :
                                        accountDetails.diagnosis.classification === 'red' ? '#d32f2f' : '#424242'
                                    )
                                }}>
                                    <div><strong style={{ color: '#9e9e9e' }}>Classification:</strong> {accountDetails.diagnosis.classification || 'N/A'}</div>
                                    <div><strong style={{ color: '#9e9e9e' }}>Explanation:</strong> {accountDetails.diagnosis.explanation || 'N/A'}</div>
                                </div>
                            </div>
                        )}

                        {accountDetails.extracted_call_data && (
                            <div style={{ marginBottom: '25px' }}>
                                <h3 style={{ borderBottom: '1px solid #444', paddingBottom: '10px', color: '#0099ff' }}>
                                    Extracted Call Data
                                </h3>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(2, 1fr)', 
                                    gap: '15px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    padding: '15px',
                                    backgroundColor: 'rgba(66, 66, 66, 0.3)',
                                    borderRadius: '6px'
                                }}>
                                    {Object.entries(accountDetails.extracted_call_data).map(([key, value]) => (
                                        <div key={key}>
                                            <strong style={{ color: '#9e9e9e' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {value || 'N/A'}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {accountDetails.transcript && (
                            <div style={{ marginBottom: '25px' }}>
                                <h3 style={{ borderBottom: '1px solid #444', paddingBottom: '10px', color: '#0099ff' }}>
                                    Transcript
                                </h3>
                                <div style={{ 
                                    whiteSpace: 'pre-wrap',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    padding: '15px',
                                    backgroundColor: 'rgba(66, 66, 66, 0.3)',
                                    borderRadius: '6px',
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    lineHeight: '1.5'
                                }}>
                                    {accountDetails.transcript}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Render the Customer Verification Form modal if triggered */}
                {showVerificationForm && (
                    <CustomerVerificationForm 
                        showModal={true}
                        onClose={() => setShowVerificationForm(false)}
                        accountData={selectedAccount}
                        accountDetails={accountDetails}
                        onSubmitSuccess={handleVerificationSubmitSuccess}
                    />
                )}
            </div>
        </div>
    );
};

export default AccountDetailsModal;
